import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { parseISO, isWithinInterval, startOfWeek, startOfMonth, format, differenceInCalendarDays, subDays } from "date-fns";
import { api } from "@/lib/api";
import { usd, num, pct, ago } from "@/lib/format";
import { KpiValue } from "@/components/common/KpiValue";
import { cn } from "@/lib/utils";
import { useUiState } from "@/lib/prefs";
import { PageHeader } from "@/components/shell/PageHeader";
import { Skeleton } from "@/components/common/primitives";
import { PerformanceChart, METRICS } from "@/components/dashboard/PerformanceChart";
import { DateRangePopover, presetRange } from "@/components/dashboard/DateRangePopover";
import { AlertTriangle, ReceiptText, TrendingUp, ArrowUpRight, ArrowDownRight, ChevronRight, GitCompareArrows, Download, Inbox, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const HEALTH = { Excess: "text-violet-500", Healthy: "text-emerald-600", Low: "text-amber-600", Critical: "text-red-600", Stockout: "text-red-600" };
const HEALTH_DOT = { Excess: "bg-signal-violet", Healthy: "bg-emerald-500", Low: "bg-amber-600", Critical: "bg-red-600", Stockout: "bg-red-600" };
const SERIES = ["gross_sales", "net_revenue", "contribution_profit", "ppc", "units"];

function quickRange(id, allStart, now = new Date()) {
  const end = new Date(now); end.setHours(0, 0, 0, 0);
  if (id === "30d") return { start: subDays(end, 29), end, label: "Last 30 days" };
  return presetRange(id, allStart, now);
}

function bucketKey(date, agg) {
  if (agg === "monthly") return format(startOfMonth(date), "MMM yyyy");
  if (agg === "weekly") return format(startOfWeek(date), "MMM d");
  return format(date, "MMM d");
}
function aggregate(rows, agg) {
  const map = new Map();
  for (const r of rows) {
    const key = bucketKey(parseISO(r.date), agg);
    const cur = map.get(key) || { label: key, gross_sales: 0, net_revenue: 0, contribution_profit: 0, ppc: 0, units: 0 };
    for (const m of SERIES) cur[m] += r[m];
    map.set(key, cur);
  }
  return [...map.values()].map((b) => ({ ...b, gross_sales: +b.gross_sales.toFixed(2), net_revenue: +b.net_revenue.toFixed(2), contribution_profit: +b.contribution_profit.toFixed(2), ppc: +b.ppc.toFixed(2) }));
}
const sum = (rows, m) => rows.reduce((a, r) => a + r[m], 0);

export default function Dashboard() {
  const uiState = useUiState();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [opps, setOpps] = useState([]);
  const [err, setErr] = useState(uiState === "error");
  const [range, setRange] = useState(null);
  const [agg, setAgg] = useState("weekly");
  const [visible, setVisible] = useState(["gross_sales", "contribution_profit"]);
  const [compare, setCompare] = useState(false);

  useEffect(() => {
    api.dashboard().then((r) => {
      setD(r.data);
      const ts = r.data.timeseries;
      const allStart = parseISO(ts[0].date);
      setRange({ start: subDays(new Date(new Date().setHours(0, 0, 0, 0)), 29), end: new Date(new Date().setHours(0, 0, 0, 0)), label: "Last 30 days", allStart });
    }).catch(() => setErr(true));
    api.topSellers().then((r) => setOpps([...r.data].sort((a, b) => b.opportunity - a.opportunity).slice(0, 6)));
  }, []);

  const ts = d?.timeseries || [];
  const span = range ? differenceInCalendarDays(range.end, range.start) + 1 : 30;
  const allowed = useMemo(() => ["daily", "weekly", "monthly"].filter((a) => (a === "daily" ? span <= 62 : a === "weekly" ? span <= 400 : span >= 21)), [span]);
  useEffect(() => { if (range && !allowed.includes(agg)) setAgg(allowed[0]); }, [allowed, agg, range]);

  const { chartData, totals } = useMemo(() => {
    if (!range || !ts.length) return { chartData: [], totals: null };
    const cur = ts.filter((r) => isWithinInterval(parseISO(r.date), { start: range.start, end: range.end }));
    const prevStart = subDays(range.start, span), prevEnd = subDays(range.end, span);
    const prev = ts.filter((r) => isWithinInterval(parseISO(r.date), { start: prevStart, end: prevEnd }));
    const cb = aggregate(cur, agg), pb = aggregate(prev, agg);
    const merged = cb.map((b, i) => { const o = { ...b }; if (compare && pb[i]) for (const m of SERIES) o[m + "__prev"] = pb[i][m]; return o; });
    const t = {}; for (const m of SERIES) { const c = sum(cur, m), p = sum(prev, m); t[m] = { value: c, delta: p ? +(((c - p) / p) * 100).toFixed(1) : 0 }; }
    const gp = t.gross_sales.value; t.margin = { value: gp ? +((t.contribution_profit.value / gp) * 100).toFixed(1) : 0, delta: 1.2 };
    return { chartData: merged, totals: t };
  }, [ts, range, agg, compare, span]);

  const toggle = (m) => setVisible((v) => v.includes(m) ? (v.length > 1 ? v.filter((x) => x !== m) : v) : [...v, m]);
  const chartState = err ? "error" : (uiState === "loading" || !d || !range) ? "loading" : (uiState === "empty" || !chartData.length) ? "empty" : "ready";
  const kpiBlank = chartState !== "ready";

  if (err && uiState !== "error") return <ErrorState onRetry={() => window.location.reload()} />;

  const kpiTiles = [
    { key: "gross_sales", label: "Gross Sales" }, { key: "net_revenue", label: "Net Revenue" },
    { key: "contribution_profit", label: "Contribution Profit" }, { key: "margin", label: "Margin", unit: "%", noSeries: true },
    { key: "units", label: "Units" }, { key: "ppc", label: "PPC Spend" },
  ];

  const feed = d ? [
    ...d.alerts.map((a) => ({ id: a.id, kind: "alert", severity: a.severity, title: a.rule, meta: `${a.product} · ${ago(a.at)}`, impact: a.severity === "critical" ? "−$210 risk" : "review", to: "/app/leads/alerts" })),
    ...d.receipts.map((r) => ({ id: r.id, kind: "receipt", severity: r.status === "needs_review" ? "moderate" : "info", title: "Receipt needs review", meta: `${r.vendor} · ${usd(r.total)}`, impact: usd(r.total), to: "/app/receipts" })),
  ] : [];

  return (
    <div className="space-y-5">
      <PageHeader title="Dashboard" description="Operational command center across sourcing and connected marketplace data." />

      {/* ===== ROW 1: performance (68%) + opportunity summary (32%) ===== */}
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.9fr)_minmax(0,1fr)] gap-5 items-stretch">
        <section className="border border-edge-subtle rounded-panel bg-surface-0 p-4 min-w-0" data-testid="performance-surface">
          {/* toolbar */}
          <div className="flex flex-wrap items-center gap-2 mb-3">
            {range && <DateRangePopover value={range} allStart={range.allStart} onApply={(r) => setRange((p) => ({ ...r, allStart: p.allStart }))} />}
            <div className="inline-flex items-center gap-1 p-1 rounded-module border border-edge-subtle max-w-full overflow-x-auto ez-scroll text-micro font-mono">
              {[["today", "Today"], ["7d", "7D"], ["30d", "30D"], ["mtd", "MTD"], ["ytd", "YTD"]].map(([id, lbl]) => (
                <button key={id} data-testid={`quick-${id}`} onClick={() => setRange((p) => ({ ...quickRange(id, p.allStart), allStart: p.allStart }))}
                  className={cn("px-2.5 h-8 ez-press", range?.label === quickRange(id, range?.allStart).label ? "ez-seg-active" : "ez-seg")}>{lbl}</button>
              ))}
            </div>
            <div className="inline-flex items-center gap-1 p-1 rounded-module border border-edge-subtle max-w-full overflow-x-auto ez-scroll text-micro font-mono" data-testid="aggregation-control">
              {["daily", "weekly", "monthly"].map((a) => (
                <button key={a} data-testid={`agg-${a}`} disabled={!allowed.includes(a)} onClick={() => setAgg(a)}
                  className={cn("px-2.5 h-8 capitalize ez-press", !allowed.includes(a) && "opacity-30 cursor-not-allowed", agg === a && allowed.includes(a) ? "ez-seg-active" : "ez-seg")}>{a}</button>
              ))}
            </div>
            <div className="ml-auto flex items-center gap-2">
              <button data-testid="compare-toggle" onClick={() => setCompare((c) => !c)} className={cn("inline-flex items-center gap-1.5 h-8 px-2.5 rounded-module text-micro ez-press", compare ? "ez-seg-active" : "ez-seg ez-seg-outline text-ink-secondary")}><GitCompareArrows className="h-3.5 w-3.5" />Compare</button>
              <button data-testid="export-btn" onClick={() => toast.success("Export queued (demo)")} className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-ctrl border border-edge-subtle text-micro hover:bg-surface-2 ez-press"><Download className="h-3.5 w-3.5" />Export</button>
            </div>
          </div>

          {/* KPI tiles (act as series toggles) */}
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-px bg-edge-subtle border border-edge-subtle rounded-ctrl overflow-hidden mb-3" data-testid="kpi-strip">
            {kpiTiles.map((k) => {
              const t = totals?.[k.key];
              const on = visible.includes(k.key);
              return (
                <button key={k.key} data-testid={`kpi-${k.key}`} onClick={() => !k.noSeries && toggle(k.key)} disabled={k.noSeries}
                  className={cn("text-left px-3 py-2 bg-surface-0 transition-colors", !k.noSeries && "hover:bg-surface-1", on && !k.noSeries && "bg-surface-1")}>
                  <div className="flex items-center gap-1.5 text-micro font-mono uppercase tracking-wide text-ink-muted">
                    {!k.noSeries && <span className="h-2 w-2 rounded-full shrink-0" style={{ background: on ? METRICS[k.key].color : "#CBD4E1" }} />}{k.label}
                  </div>
                  <div className="mt-1 metric-result">
                    {kpiBlank || !totals ? (
                      <span className="metric-value font-mono text-[17px] font-semibold text-ink-primary leading-none">—</span>
                    ) : k.unit === "%" ? (
                      <span className="metric-value font-mono text-[17px] font-semibold text-ink-primary leading-none">{pct(t.value)}</span>
                    ) : k.key === "units" ? (
                      <span className="metric-value font-mono text-[17px] font-semibold text-ink-primary leading-none">{num(Math.round(t.value))}</span>
                    ) : (
                      <KpiValue value={t.value} label={k.label} className="font-mono text-[17px] font-semibold text-ink-primary leading-none" />
                    )}
                    {t && !kpiBlank && <span className={cn("metric-trend inline-flex items-center text-micro font-mono", t.delta >= 0 ? "text-emerald-600" : "text-red-600")}>{t.delta >= 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}{Math.abs(t.delta)}%</span>}
                  </div>
                </button>
              );
            })}
          </div>

          <PerformanceChart data={chartData} visibleMetrics={visible} compare={compare} state={chartState} height={248} />
          <div className="mt-2 flex items-center justify-between text-micro text-ink-muted">
            <span>{range?.label} · {agg} · {compare ? "vs previous period" : "no comparison"}</span>
            <span className="inline-flex items-center gap-1"><RefreshCw className="h-3 w-3" />Updated just now</span>
          </div>
        </section>

        {/* Opportunity summary */}
        <section className="border border-edge-subtle rounded-panel bg-surface-0 p-4 min-w-0" data-testid="opportunity-summary">
          <h2 className="text-stitle text-ink-primary mb-3">Opportunity summary</h2>
          <div className="grid grid-cols-2 gap-3">
            {[
              { l: "New opportunities", v: opps.filter((o) => o.roi >= 30).length, sub: "ROI ≥ 30%" },
              { l: "Newly profitable", v: 2, sub: "this week" },
              { l: "Average ROI", v: opps.length ? pct(+(opps.reduce((a, o) => a + o.roi, 0) / opps.length).toFixed(1)) : "—", sub: "top results" },
              { l: "Est. available profit", v: usd(Math.round(opps.reduce((a, o) => a + o.net_profit, 0) * 24)), sub: "at test qty" },
            ].map((s) => (
              <div key={s.l} className="rounded-ctrl bg-surface-1 border border-edge-subtle p-3">
                <div className="text-micro font-mono uppercase text-ink-muted">{s.l}</div>
                <div className="mt-1 font-mono tabnum text-[20px] font-semibold text-ink-primary">{s.v}</div>
                <div className="text-micro text-ink-muted">{s.sub}</div>
              </div>
            ))}
          </div>
          <button onClick={() => nav("/app/research/top")} className="mt-3 w-full h-9 rounded-ctrl bg-emerald-600 text-white text-compact ez-glow-emerald inline-flex items-center justify-center gap-1.5"><TrendingUp className="h-4 w-4" />Explore opportunities</button>
        </section>
      </div>

      {/* ===== ROW 2: Needs attention (62%) + Inventory risk (38%) ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-5 items-start">
        <section data-testid="attention-feed" className="min-w-0">
          <div className="flex items-center justify-between mb-2"><h2 className="text-stitle text-ink-primary">Needs attention</h2><span className="text-micro font-mono text-ink-muted">{feed.length} items</span></div>
          <div className="border border-edge-subtle rounded-panel bg-surface-0 overflow-hidden">
            {feed.length === 0 ? <EmptyRow label="Nothing needs attention right now." /> : feed.map((f, i) => {
              const clr = f.severity === "critical" ? "text-red-600" : f.severity === "moderate" ? "text-amber-600" : "text-signal-blue";
              const Icon = f.kind === "receipt" ? ReceiptText : AlertTriangle;
              return (
                <button key={f.id} onClick={() => nav(f.to)} data-testid={`attention-${f.id}`} className={cn("w-full flex items-center gap-3 px-3 h-12 text-left hover:bg-surface-2 transition-colors", i !== feed.length - 1 && "border-b border-edge-subtle")}>
                  <Icon className={cn("h-4 w-4 shrink-0", clr)} />
                  <div className="min-w-0 flex-1"><div className="text-compact text-ink-primary truncate">{f.title}</div><div className="text-micro text-ink-muted truncate">{f.meta}</div></div>
                  <span className={cn("text-micro font-mono shrink-0 hidden sm:block", clr)}>{f.impact}</span>
                  <ChevronRight className="h-4 w-4 text-ink-muted shrink-0" />
                </button>
              );
            })}
          </div>
        </section>

        <section data-testid="dashboard-inventory" className="min-w-0">
          <div className="flex items-center justify-between mb-2"><h2 className="text-stitle text-ink-primary">Inventory risk</h2><button onClick={() => nav("/app/inventory")} className="text-micro font-mono text-signal-blue">All inventory</button></div>
          <div className="border border-edge-subtle rounded-panel bg-surface-0 overflow-x-auto ez-scroll">
            <table className="w-full text-compact">
              <thead><tr className="text-micro font-mono uppercase text-ink-muted text-left bg-surface-2"><th className="py-2 px-3">SKU</th><th className="px-3 text-right">Avail</th><th className="px-3 text-right">Days</th><th className="px-3 text-right">Reorder</th><th className="px-3">Risk</th></tr></thead>
              <tbody>
                {(d?.inventory || []).map((row) => (
                  <tr key={row.sku} className="border-t border-edge-subtle hover:bg-surface-2 transition-colors">
                    <td className="py-2 px-3"><div className="font-mono text-micro text-ink-primary">{row.sku}</div><div className="text-micro text-ink-muted truncate max-w-[150px]">{row.title}</div></td>
                    <td className="px-3 text-right font-mono">{row.available}</td>
                    <td className={cn("px-3 text-right font-mono", row.days_supply <= 14 ? "text-red-600" : row.days_supply >= 200 ? "text-violet-500" : "text-ink-primary")}>{row.days_supply}</td>
                    <td className="px-3 text-right font-mono">{row.days_supply <= 30 ? Math.max(24, row.available) : "—"}</td>
                    <td className="px-3"><span className={cn("inline-flex items-center gap-1.5 text-micro font-mono", HEALTH[row.health])}><span className={cn("h-1.5 w-1.5 rounded-full", HEALTH_DOT[row.health])} />{row.health}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      {/* ===== ROW 3: research activity ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-5 items-start">
        <section data-testid="opportunity-radar" className="min-w-0">
          <div className="flex items-center justify-between mb-2"><h2 className="text-stitle text-ink-primary">Opportunity radar</h2><button onClick={() => nav("/app/research/top")} className="text-micro font-mono text-signal-blue">Open finder</button></div>
          <div className="border border-edge-subtle rounded-panel bg-surface-0 overflow-x-auto ez-scroll">
            <table className="w-full text-compact">
              <thead><tr className="text-micro font-mono uppercase text-ink-muted text-left bg-surface-2"><th className="py-2 px-3">Product</th><th className="px-3">Category</th><th className="px-3 text-right">Est. sales/mo</th><th className="px-3 text-right">Price</th><th className="px-3 text-right">ROI</th><th className="px-3 text-right">Opp.</th><th className="px-3" /></tr></thead>
              <tbody>
                {opps.map((o) => (
                  <tr key={o.id} onClick={() => nav("/app/analyzer")} className="border-t border-edge-subtle hover:bg-surface-2 transition-colors cursor-pointer" data-testid={`radar-${o.id}`}>
                    <td className="py-2 px-3 text-ink-primary truncate max-w-[240px]">{o.title}</td>
                    <td className="px-3 text-ink-secondary">{o.category}</td>
                    <td className="px-3 text-right font-mono">{num(o.est_sales)}</td>
                    <td className="px-3 text-right font-mono">{usd(o.price)}</td>
                    <td className={cn("px-3 text-right font-mono", o.roi >= 30 ? "text-emerald-600" : "text-amber-600")}>{pct(o.roi)}</td>
                    <td className="px-3 text-right font-mono text-ink-primary">{o.opportunity}</td>
                    <td className="px-3 text-right"><button onClick={(e) => { e.stopPropagation(); nav("/app/analyzer"); }} className="text-micro font-mono text-signal-blue inline-flex items-center gap-0.5">Analyze <ArrowUpRight className="h-3 w-3" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section data-testid="recent-activity" className="min-w-0">
          <h2 className="text-stitle text-ink-primary mb-2">Recent activity</h2>
          <div className="border border-edge-subtle rounded-panel bg-surface-0 overflow-hidden">
            {[
              { i: TrendingUp, c: "text-emerald-600", t: "Member's Mark Water became profitable", m: "ROI 68.5% · 2h ago" },
              { i: AlertTriangle, c: "text-amber-600", t: "Seller count +2 on Ozark Trail Tumbler", m: "now 4 sellers · 5h ago" },
              { i: ReceiptText, c: "text-signal-blue", t: "Analyzed Crayola Markers 40ct", m: "Verdict: REVIEW · 8h ago" },
              { i: TrendingUp, c: "text-red-600", t: "Price dropped 12% on Great Value Coffee", m: "$12.42 → $10.93 · 1d ago" },
              { i: AlertTriangle, c: "text-signal-blue", t: "Saved search matched 3 new products", m: "Grocery replenishables · 1d ago" },
            ].map((r, i, arr) => { const Icon = r.i; return (
              <div key={i} className={cn("flex items-center gap-3 px-3 h-12", i !== arr.length - 1 && "border-b border-edge-subtle")}>
                <Icon className={cn("h-4 w-4 shrink-0", r.c)} />
                <div className="min-w-0 flex-1"><div className="text-compact text-ink-primary truncate">{r.t}</div><div className="text-micro text-ink-muted truncate">{r.m}</div></div>
              </div>
            ); })}
          </div>
        </section>
      </div>
    </div>
  );
}

function EmptyRow({ label }) {
  return <div className="flex items-center gap-2 px-3 h-16 text-compact text-ink-muted"><Inbox className="h-4 w-4" />{label}</div>;
}
function ErrorState({ onRetry }) {
  return (
    <div className="grid place-items-center h-[60vh]">
      <div className="text-center"><AlertTriangle className="h-8 w-8 text-red-600 mx-auto mb-3" /><div className="text-stitle text-ink-primary">Couldn't load the dashboard</div>
        <p className="text-compact text-ink-muted mt-1">Demo data adapter failed to respond.</p>
        <button onClick={onRetry} className="mt-4 h-9 px-4 rounded-ctrl bg-emerald-600 text-white text-compact">Retry</button></div>
    </div>
  );
}
